import React from 'react';
import classnames from 'classnames';

import Icon from 'dpl/common/components/Icon';
import {
  COMMON_FORM_FIELD_CLASSES,
  FORM_FIELD_VARIANT_TYPES
} from 'dpl/shared/form/utils/constants';

import Label from './Label';
import ErrorWrapper from './ErrorWrapper';

type SelectOptionValue = string | number | Date;

interface SelectOption {
  value?: SelectOptionValue;
  id?: string | number;
  name?: string | number;
  title?: string;
}

interface SimpleSelectProps
  extends Omit<
    React.SelectHTMLAttributes<HTMLSelectElement>,
    'name' | 'onChange' | 'value'
  > {
  testId?: string;
  disablePlaceholder?: boolean;
  disabled?: boolean;
  displayErrors?: boolean;
  errors?: React.ReactNode[];
  id?: string;
  label?: string;
  name?: string;
  onChange?: (event: React.ChangeEvent<HTMLSelectElement>) => void;
  options?: Array<SelectOption | string>;
  placeholder?: string;
  value?: SelectOptionValue | null;
  allowZeroValue?: boolean;
  variant?: string;
}

const ARROW_SIZES: Record<string, string> = {
  [FORM_FIELD_VARIANT_TYPES.LARGE]: '32px',
  [FORM_FIELD_VARIANT_TYPES.REGULAR]: '24px'
};

const VARIANT_CLASSES: Record<string, string> = {
  [FORM_FIELD_VARIANT_TYPES.LARGE]: `${COMMON_FORM_FIELD_CLASSES} pv4 pv5-md pl4 pr10 f4 fw-medium`,
  [FORM_FIELD_VARIANT_TYPES.REGULAR]: `${COMMON_FORM_FIELD_CLASSES} pv3 ph4 font-16`
};

function normalizeValue(value: SelectOptionValue | null | undefined): string {
  if (value === undefined || value === null) return '';
  return String(value);
}

function normalizeOptionValue(option: SelectOption): string {
  const rawValue = option.value ?? option.id ?? '';
  return String(rawValue);
}

export default function SimpleSelect({
  testId = 'form-simple-select',
  disablePlaceholder = false,
  disabled = false,
  displayErrors = false,
  errors = [],
  id,
  label,
  name,
  onChange,
  options = [],
  placeholder = 'Select',
  value = null,
  variant = FORM_FIELD_VARIANT_TYPES.REGULAR,
  allowZeroValue = false,
  ...selectProps
}: SimpleSelectProps) {
  const arrowSize = ARROW_SIZES[variant];

  return (
    <div
      className="Select w-100 relative overflow-hidden"
      data-test-id={testId}
    >
      {label && <Label newDesignSystemStyles htmlFor={id} text={label} />}
      <ErrorWrapper errors={errors} isShown={displayErrors}>
        <div className="relative">
          <select
            data-test-id="form-select"
            disabled={disabled || !options.length}
            className={classnames(VARIANT_CLASSES[variant], {
              disabled: allowZeroValue
                ? value === undefined || value === null || value === ''
                : !value
            })}
            id={id}
            name={name}
            onChange={onChange}
            value={normalizeValue(value)}
            {...selectProps}
          >
            <option value="" disabled={disablePlaceholder}>
              {placeholder}
            </option>
            {
              // TODO: consolidate option value structure
              options.map(option => {
                const normalizedOption =
                  typeof option === 'string'
                    ? { value: option, title: option }
                    : option;

                const optionValue = normalizeOptionValue(normalizedOption);
                return (
                  <option value={optionValue} key={optionValue}>
                    {normalizedOption.title || normalizedOption.name}
                  </option>
                );
              })
            }
          </select>
          <Icon
            className="mr2 z-3 pointer-events-none transform-center right-0"
            height={arrowSize}
            name="fetch-chevron-down"
            width={arrowSize}
          />
        </div>
      </ErrorWrapper>
    </div>
  );
}
